import * as React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { css } from "@emotion/core";
import { Breadcrumbs, Breadcrumb, BreadcrumbProps } from "@blueprintjs/core";

type Survey = {
  id: number;
  label: string;
  folder_id: number | null;
};

type Folder = {
  id: number;
  label: string;
};

type NavigatorTabsProps = {
  surveys: Survey[];
  folders: Folder[];
  selectedSurveyId: number | null;
  setSelectedSurveyId: (id: number | null) => void;
};

const NavigatorTabs = ({
  surveys,
  folders,
  selectedSurveyId,
  setSelectedSurveyId,
}: NavigatorTabsProps) => {
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const goToAllFolders = React.useCallback(() => {
    navigate("/folders");
    setSelectedSurveyId(null);
  }, [navigate, setSelectedSurveyId]);

  const currentSurvey = surveys.find(
    (survey: Survey) => survey.id === selectedSurveyId
  );
  const folderHash = folders.reduce<Record<number, Folder>>(
    (all: Record<number, Folder>, folder: Folder) => ({
      ...all,
      [folder.id]: folder,
    }),
    {}
  );

  const singleSurvey = (): BreadcrumbProps[] => {
    const routeSurveyId = params.id ? Number(params.id) : null;
    if (!currentSurvey || currentSurvey.id !== routeSurveyId) {
      console.warn(
        "Breadcrumb mismatch: currentSurvey does not match route param"
      );
      return allFolders();
    }

    return [
      {
        onClick: goToAllFolders,
        icon: "folder-open",
        text: "All Folders",
      },
      {
        onClick: () => {
          navigate(`/folders/${currentSurvey.folder_id}`);
          setSelectedSurveyId(null);
        },
        icon: "folder-open",
        text:
          (currentSurvey.folder_id &&
            folderHash[currentSurvey.folder_id] &&
            folderHash[currentSurvey.folder_id].label) ||
          "Unsorted",
      },
      {
        icon: "geosearch",
        text: currentSurvey.label,
      },
    ];
  };

  const singleFolder = (): BreadcrumbProps[] => [
    {
      onClick: goToAllFolders,
      icon: "folder-open",
      text: "All Folders",
    },
    {
      onClick: () => {
        navigate(`/folders/${params.id}`);
        setSelectedSurveyId(null);
      },
      icon: "folder-open",
      text: params.id ? folderHash[Number(params.id)]?.label : undefined,
    },
  ];

  const allFolders = (): BreadcrumbProps[] => [
    {
      onClick: goToAllFolders,
      icon: "folder-close",
      text: "All Folders",
    },
  ];

  const allSurveys = (): BreadcrumbProps[] => [
    {
      onClick: goToAllFolders,
      icon: "folder-open",
      text: "All Folders",
    },
    {
      icon: "folder-open",
      text: "All Surveys",
    },
  ];

  const getItems = () => {
    if (location.pathname === "/surveys") {
      return allSurveys();
    }

    if (
      location.pathname.startsWith("/folders/") &&
      params.id &&
      params.id !== "all"
    ) {
      return singleFolder();
    }

    if (location.pathname === "/folders") {
      return allFolders();
    }

    // Only show survey breadcrumbs when actually on a survey route
    if (location.pathname.startsWith("/surveys/") && currentSurvey) {
      return singleSurvey();
    }

    // Fallback to all folders for any unmatched route
    return allFolders();
  };

  const items = getItems();

  return (
    <div
      css={css`
        padding: 0.25rem 1rem;
        background: var(--background-gradient);
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid var(--twilight);

        & .bp6-breadcrumb {
          font-size: 13px;
          line-height: 1;
        }

        & .bp6-breadcrumbs {
          height: 24px;
        }

        & svg {
          width: 13px;
          height: 13px;
          margin-right: 4px;
        }
      `}
    >
      <Breadcrumbs
        css={css`
          & > li:last-of-type {
            flex-grow: 1;
          }
        `}
        currentBreadcrumbRenderer={(props: BreadcrumbProps) => (
          <Breadcrumb {...props} />
        )}
        items={items}
      />
    </div>
  );
};

export default NavigatorTabs;
